import { useEffect, useState } from "react";
import { type ClienteNexo, type StatsNexo, vizStats } from "../api.ts";
import { C, autoridadEs, fecha, entero, flagVista, programaVista, schemaEs, st } from "../theme.ts";
import { BarrasH, Cargando, ChipSchema, ErrorBox, StatTile, celdaTd, celdaTh } from "../ui/componentes.tsx";

/**
 * Panorama — el estado vivo del grafo: cifras hero, fuentes con su última
 * carga, distribuciones por tipo/programa/país y la matriz de relaciones
 * origen→destino (la lectura típica de cada dirección de arista).
 */
export function Panorama({ cliente }: { cliente: ClienteNexo }) {
  const [stats, setStats] = useState<StatsNexo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    vizStats(cliente)
      .then((s) => {
        if (vivo) setStats(s);
      })
      .catch((e: unknown) => {
        if (vivo) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      vivo = false;
    };
  }, [cliente]);

  if (error) return <ErrorBox mensaje={error} />;
  if (!stats) return <Cargando texto="Cargando el panorama…" />;

  const lecturaMatriz: Record<string, string> = {
    "Person→Organization": "el operador relacionado con su cártel/red",
    "Organization→Organization": "fachada relacionada con la matriz o con otra fachada",
    "Organization→Person": "la empresa relacionada con su controlador",
    "Person→Person": "familiares, lugartenientes, prestanombres",
    "Vessel→Organization": "buque relacionado con su dueño",
    "Aircraft→Organization": "aeronave relacionada con su dueño",
    "Vessel→Person": "buque relacionado con su dueño",
    "Aircraft→Person": "aeronave relacionada con su dueño",
  };

  const saludVista = {
    ok: { etiqueta: "Al día", color: C.green, fondo: C.greenBg },
    alerta: { etiqueta: "Revisar", color: C.amber, fondo: C.amberBg },
    critico: { etiqueta: "Detenida", color: C.red, fondo: C.redBg },
  } as const;

  // Tipo de relación (schema de la arista) → etiqueta + lectura. Las tipadas
  // vienen del Advanced XML de OFAC (0089); LinkedTo genérica sale de Remarks.
  const relTipoVista: Record<string, { etiqueta: string; lectura: string }> = {
    Ownership: { etiqueta: "Propiedad / control", lectura: "dueño o controlador (sirve para saber si una empresa cuenta como sancionada)" },
    Directorship: { etiqueta: "Dirección", lectura: "líder, oficial o ejecutivo de la entidad" },
    Family: { etiqueta: "Familiar", lectura: "familiar según OFAC (hijo, cónyuge…)" },
    Membership: { etiqueta: "Membresía", lectura: "rol significativo en la organización" },
    Associate: { etiqueta: "Asociado", lectura: "asociado de" },
    LinkedTo: { etiqueta: "Relación (Linked To)", lectura: "relación declarada; tipada o de Remarks" },
    UnknownLink: { etiqueta: "Relación sin tipo", lectura: "relación no clasificada" },
  };
  // Suma tipada + genérica por schema para el desglose (las LinkedTo mezclan ambas).
  const relPorTipo = (() => {
    const acc = new Map<string, { n: number; tipadas: number }>();
    for (const r of stats.por_arista_tipo ?? []) {
      const cur = acc.get(r.schema) ?? { n: 0, tipadas: 0 };
      cur.n += r.n;
      if (r.tipada) cur.tipadas += r.n;
      acc.set(r.schema, cur);
    }
    return [...acc.entries()].map(([schema, v]) => ({ schema, ...v })).sort((a, b) => b.n - a.n);
  })();

  return (
    <div>
      <h1 style={{ ...st.display, fontSize: 26, margin: "4px 0 14px" }}>Panorama</h1>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <StatTile etiqueta="Entidades vigentes" valor={stats.entidades} />
        <StatTile etiqueta="Sanciones activas" valor={stats.sanciones_activas} detalle="eje red (OFAC)" />
        <StatTile etiqueta="Flags fiscales" valor={stats.flags_activos} detalle="eje propio (listas del SAT)" />
        <StatTile etiqueta="Relaciones" valor={stats.aristas} />
        <StatTile etiqueta="Nombres y alias" valor={stats.nombres} />
        <StatTile etiqueta="Identificadores" valor={stats.identificadores} />
      </div>

      <div style={st.tarjeta}>
        <p style={st.eyebrow}>Fuentes</p>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={celdaTh}>Fuente</th>
              <th style={celdaTh}>Autoridad</th>
              <th style={celdaTh}>Cadencia</th>
              <th style={celdaTh}>Última carga</th>
              <th style={celdaTh}>Salud</th>
            </tr>
          </thead>
          <tbody>
            {stats.fuentes.map((f) => {
              const salud = saludVista[f.salud];
              return <tr key={f.id}>
                <td style={celdaTd}>{f.nombre}</td>
                <td style={celdaTd}>{autoridadEs(f.autoridad)}</td>
                <td style={celdaTd}>{f.cadencia === "2x_dia" ? "2× al día" : f.cadencia ?? "—"}</td>
                <td style={celdaTd}>{f.ultima_carga ? fecha(f.ultima_carga) : "—"}</td>
                <td style={celdaTd}>
                  <span
                    title={f.ultimo_detalle ?? `Último estado: ${f.ultimo_estado ?? "sin cargas"}`}
                    style={{ display: "inline-block", padding: "3px 8px", borderRadius: 999,
                      fontSize: 11, fontWeight: 700, color: salud.color, background: salud.fondo }}
                  >
                    {salud.etiqueta}
                  </span>
                  {f.pendientes > 0 ? <span style={{ marginLeft: 6, color: C.muted, fontSize: 11 }}>{f.pendientes} pendiente(s)</span> : null}
                </td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>

      {/* 2×2 fijo en desktop (min() colapsa a una columna en pantallas angostas):
          con auto-fit puro salían 3 columnas y la tabla de relaciones quedaba apretada. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 460px), 1fr))", gap: 12 }}>
        <div style={{ ...st.tarjeta, marginBottom: 0 }}>
          <p style={st.eyebrow}>Entidades por tipo</p>
          <BarrasH
            datos={stats.por_schema.map((d) => ({
              etiqueta: <ChipSchema schema={d.schema} />,
              titulo: schemaEs(d.schema),
              n: d.n,
            }))}
          />
        </div>

        <div style={{ ...st.tarjeta, marginBottom: 0 }}>
          <p style={st.eyebrow}>Programas y listas con más registros</p>
          <BarrasH
            datos={stats.por_programa.map((d) => ({
              etiqueta: programaVista(d.programa).etiqueta,
              titulo: `${programaVista(d.programa).etiqueta} (${autoridadEs(d.autoridad)})`,
              n: d.n,
            }))}
          />
          <p style={{ fontSize: 11.5, color: C.muted, margin: "8px 0 0" }}>
            Mezcla los dos ejes solo para dimensionar: sanciones (OFAC) y listas fiscales del SAT.
            El detalle del eje fiscal está a la derecha.
          </p>
        </div>

        <div style={{ ...st.tarjeta, marginBottom: 0 }}>
          <p style={st.eyebrow}>Situación fiscal SAT — eje propio</p>
          <BarrasH
            color={C.amber}
            datos={stats.por_flag.map((d) => ({
              etiqueta: flagVista(d).etiqueta,
              titulo: `${flagVista(d).etiqueta} · severidad ${d.severidad}`,
              n: d.n,
            }))}
          />
          <p style={{ fontSize: 11.5, color: C.muted, margin: "8px 0 0" }}>
            RFC en las listas del SAT (artículo 69 y empresas que emiten facturas falsas). No son sanciones:
            informan la diligencia sobre la persona o empresa (severidad del catálogo: fuerte · media · débil ·
            exculpatoria).
          </p>
        </div>

        <div style={{ ...st.tarjeta, marginBottom: 0 }}>
          <p style={st.eyebrow}>Entidades por país</p>
          <BarrasH
            datos={stats.por_pais.map((d) => ({
              etiqueta: d.pais,
              titulo: d.pais,
              n: d.n,
            }))}
          />
          <p style={{ fontSize: 11.5, color: C.muted, margin: "8px 0 0" }}>
            "sin país" agrupa a personas y empresas de la lista de OFAC sin país estructurado en el CSV clásico (mejora en F2 con el XML avanzado).
          </p>
        </div>

        {relPorTipo.length > 0 ? (
          <div style={{ ...st.tarjeta, marginBottom: 0 }}>
            <p style={st.eyebrow}>Relaciones por tipo</p>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={celdaTh}>Tipo</th>
                  <th style={{ ...celdaTh, textAlign: "right" }}>Relaciones</th>
                  <th style={celdaTh}>Lectura</th>
                </tr>
              </thead>
              <tbody>
                {relPorTipo.map((r) => {
                  const v = relTipoVista[r.schema] ?? { etiqueta: r.schema, lectura: "—" };
                  return (
                    <tr key={r.schema}>
                      <td style={{ ...celdaTd, fontWeight: 600 }}>
                        {v.etiqueta}
                        {r.tipadas > 0 && r.tipadas < r.n ? (
                          <span style={{ color: C.muted, fontSize: 11, fontWeight: 400 }}>
                            {" "}({entero(r.tipadas)} tipadas)
                          </span>
                        ) : null}
                      </td>
                      <td style={{ ...celdaTd, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{entero(r.n)}</td>
                      <td style={{ ...celdaTd, color: C.muted, fontSize: 12.5 }}>{v.lectura}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p style={{ fontSize: 11.5, color: C.muted, margin: "8px 0 0" }}>
              Tipo y dirección vienen del Advanced XML de OFAC (propiedad, dirección, familia…).
              La matriz de abajo agrupa las mismas relaciones por tipo de entidad.
            </p>
          </div>
        ) : null}

        <div style={{ ...st.tarjeta, marginBottom: 0 }}>
          <p style={st.eyebrow}>Relaciones: origen → destino</p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={celdaTh}>Origen → destino</th>
                <th style={{ ...celdaTh, textAlign: "right" }}>Relaciones</th>
                <th style={celdaTh}>Lectura típica</th>
              </tr>
            </thead>
            <tbody>
              {stats.aristas_matriz.map((m, i) => (
                <tr key={i}>
                  <td style={{ ...celdaTd, whiteSpace: "nowrap" }}>
                    <ChipSchema schema={m.src} />
                    <span style={{ color: C.muted, margin: "0 6px" }}>→</span>
                    <ChipSchema schema={m.dst} />
                  </td>
                  <td style={{ ...celdaTd, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{entero(m.n)}</td>
                  <td style={{ ...celdaTd, color: C.muted, fontSize: 12.5 }}>
                    {lecturaMatriz[`${m.src}→${m.dst}`] ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
